import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { ArrowRight, Sparkles } from "lucide-react";

/**
 * V2.0.89 — Homepage showcase teaser.
 *
 * A single-section card block that fetches the top-3 transformations
 * from /api/discover/best-transformations and links to the full
 * /best-transformations page. Silent-fails if the endpoint is empty
 * (renders nothing so the homepage doesn't show a broken shell).
 */
export default function HomeShowcaseCard() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/discover/best-transformations`, { params: { limit: 3 } });
        if (!cancel) setItems(r.data?.items || []);
      } catch { /* silently drop — this card is optional */ }
    })();
    return () => { cancel = true; };
  }, []);

  if (items === null || items.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24"
      data-testid="home-showcase-teaser"
    >
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-3 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            // Sketch to real 3D home
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-zinc-50 tracking-tight">
            Real users. Real sketches.
          </h2>
          <p className="font-body text-base text-zinc-400 mt-3 max-w-xl leading-relaxed">
            Every design here started as a hand-drawn floor plan, phone photo, or PDF export.
            Blueprint AI turned each one into an editable 3D BIM home.
          </p>
        </div>
        <Link
          to="/best-transformations"
          data-testid="home-showcase-cta"
          className="inline-flex items-center gap-2 border border-brass-700/60 hover:border-brass-500 text-brass-300 hover:text-brass-200 font-mono text-[10px] uppercase tracking-widest px-5 py-3 transition-colors"
        >
          See weekly top 5 <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((item, i) => (
          <li
            key={item.share_slug}
            className="border border-zinc-800 hover:border-brass-500/40 group transition-colors"
            data-testid={`home-showcase-card-${i + 1}`}
          >
            <Link to={item.share_url} className="block">
              <div className="aspect-[16/9] bg-zinc-900 overflow-hidden">
                <img
                  src={`${API}/share/${item.share_slug}/og-image.png`}
                  alt={`Before/after transformation ${i + 1}: ${item.name || "home design"}`}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <div className="font-display text-lg text-zinc-100 truncate">
                  {item.name || "Untitled"}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                  {(item.square_footage || 0).toLocaleString()} sqft · {item.bedrooms ?? "?"}BR / {item.bathrooms ?? "?"}BA
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
